import { ReactElement, useState } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react/types/components/Link';

import { icon_chevron_small_down, icon_chevron_small_up } from '@gn-audio/icons';
import { FlatTitledSitecoreItem, getTitle } from '@gn-audio/jss-core';

import { Accordion } from '../Accordion/Accordion';
import { JabraIcon } from '../JabraIcon/JabraIcon';
import { DISPLAY_VERSIONS } from '../MegaMenu';

type LinkItem = FlatTitledSitecoreItem<{
  Link: { value: LinkFieldValue };
  linkAriaLabel?: { value: string };
  id: string;
  displayName?: string;
}>;

export interface ImageValue {
  src: string;
  alt: string;
  width: string;
  height: string;
}

export interface FooterBottomData {
  AlternativeText: {
    value: string | '';
  };
  Links: LinkItem[];
  Copyright: {
    value: string | '';
  };
  Image: {
    value: ImageValue;
  };
  id?: string;
  displayName?: string;
}

export interface FooterColumnData {
  Headline: { value: string };
  Links: LinkItem[];
  id: string;
  displayName?: string;
}

type FooterBottom = FooterBottomData;
type FooterColumn = FooterColumnData;
type FooterColumns = FooterColumn[];

export type FooterProps = {
  fields: {
    Columns: FooterColumns;
    Bottom: FooterBottom;
  };
};

const getLinkValue = (item: LinkItem) => item.Link?.value as LinkFieldValue;

const FooterColumn = ({ column }: { column: FooterColumn }): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const headline = column.Headline?.value;
  return (
    <>
      <div key={column.displayName} className="flex flex-[1_0_250px] flex-col gap-4 max-md:hidden">
        <h2 className="font-bold">{headline}</h2>
        {/* todo: add translation for label */}
        <ul className="flex flex-col gap-1" aria-label={headline}>
          {column.Links?.filter((link) => !!getLinkValue(link)?.href).map((link) => {
            const linkField = getLinkValue(link);

            return (
              <li key={linkField.href}>
                <a
                  href={linkField.href}
                  className="text-sm"
                  {...(link.linkAriaLabel?.value && {
                    'aria-label': link.linkAriaLabel.value,
                  })}
                >
                  {getTitle(link)}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      <div
        key={`${column.displayName} ${DISPLAY_VERSIONS.MOBILE}`}
        className="flex flex-[1_0_100%] flex-col gap-4 md:hidden"
      >
        <Accordion
          name={column.id}
          onExpandChange={(expanded) => setExpanded(expanded)}
          renderHeading={(expanded) => {
            return (
              <div className="flex items-center font-bold">
                <h3 className="flex-1">{headline}</h3>
                {expanded ? (
                  <span className="accordion-icon">
                    <JabraIcon className="h-3 w-3" Icon={icon_chevron_small_up} />
                  </span>
                ) : (
                  <span className="accordion-icon">
                    <JabraIcon className="h-3 w-3" Icon={icon_chevron_small_down} />
                  </span>
                )}
              </div>
            );
          }}
        >
          <ul className="flex flex-col gap-1 p-1">
            {column.Links?.filter((link) => !!getLinkValue(link)?.href).map((link) => {
              const linkValue = getLinkValue(link);
              return (
                <li key={linkValue.href}>
                  <a
                    href={linkValue.href}
                    className="text-sm"
                    tabIndex={expanded ? 0 : -1}
                    {...(link.linkAriaLabel?.value && {
                      'aria-label': link.linkAriaLabel.value,
                    })}
                  >
                    {getTitle(link)}
                  </a>
                </li>
              );
            })}
          </ul>
        </Accordion>
      </div>
    </>
  );
};

const LinksSection = ({ columns }: { columns: FooterColumns }) => {
  return (
    <div className="grid grid-cols-1 gap-x-[1.5rem] pb-4 pt-12 max-md:gap-y-6 md:grid-cols-2 md:gap-y-16 lg:grid-cols-3 xl:grid-cols-4">
      {columns.map((column) => (
        <div key={column.id}>
          <FooterColumn column={column} />
        </div>
      ))}
    </div>
  );
};

const BottomSection = ({ data }: { data: FooterBottom }) => {
  const imageField = data.Image;

  return (
    <div className="py-12">
      <div className="border-t border-gray-100 pt-12">
        {!!imageField && <Image field={imageField} />}
        <ul className="flex flex-col flex-wrap gap-x-4 pt-6 xxs:flex-row">
          {data.Links?.filter((link) => !!getLinkValue(link)?.href).map((link) => {
            const linkField = getLinkValue(link);
            const title = link.Title?.value || link.TitleDictionary?.Phrase?.value || '';

            return (
              <li key={linkField.href}>
                <a
                  href={linkField.href}
                  className="text-xs leading-5"
                  {...(link.linkAriaLabel?.value && {
                    'aria-label': link.linkAriaLabel.value,
                  })}
                >
                  {title}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export const Footer = (props: FooterProps): ReactElement => {
  const columns = props.fields.Columns;
  const data = props.fields.Bottom;

  return (
    <footer className="bg-theme relative">
      <div className="container-padding container py">
        {columns && <LinksSection columns={columns} />}
        {data && <BottomSection data={data} />}
      </div>
    </footer>
  );
};
